import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  MapPin,
  Phone,
  ExternalLink,
  Clock,
  Navigation2,
  Building2,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import SEO from "../components/SEO";

function ShelterCard({ s }) {
  const dirQuery = encodeURIComponent(s.address || `${s.name} ${s.city} ${s.state}`);
  const dirUrl = `https://www.google.com/maps/dir/?api=1&destination=${dirQuery}`;
  return (
    <div
      className="border border-zinc-900 bg-white p-5 flex flex-col hover-shadow-harsh"
      data-testid={`shelter-card-${s.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-11 h-11 border border-zinc-300 bg-zinc-50 flex items-center justify-center">
          <Building2 size={20} className="text-zinc-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow flex items-center gap-2 flex-wrap">
            <span>
              <MapPin size={10} className="inline mr-1 -mt-0.5" />
              {s.city}{s.city ? ", " : ""}{s.state_code || s.state}
            </span>
            {typeof s.distance_mi === "number" && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 border border-red-700 bg-red-50 text-red-700 normal-case tracking-normal"
                data-testid={`shelter-distance-${s.id}`}
              >
                <Navigation2 size={10} />
                {s.distance_mi} mi away
              </span>
            )}
          </div>
          <h3 className="font-display font-black text-lg leading-tight mt-1" data-testid={`shelter-name-${s.id}`}>
            {s.name}
          </h3>
        </div>
      </div>

      {(s.address || s.phone) && (
        <div className="mt-3 space-y-1.5 text-sm">
          {s.address && (
            <div className="flex items-start gap-2 text-zinc-700">
              <MapPin size={13} className="mt-0.5 flex-shrink-0 text-zinc-400" />
              <span data-testid={`shelter-address-${s.id}`}>{s.address}</span>
            </div>
          )}
          {s.phone && (
            <div className="flex items-center gap-2 text-zinc-700">
              <Phone size={13} className="flex-shrink-0 text-zinc-400" />
              <a href={`tel:${s.phone.replace(/[^0-9+]/g, "")}`} className="hover:text-red-600 font-mono">
                {s.phone}
              </a>
            </div>
          )}
          {s.hours && (
            <div className="flex items-start gap-2 text-zinc-700">
              <Clock size={13} className="mt-0.5 flex-shrink-0 text-zinc-400" />
              <span className="text-[13px]">{s.hours}</span>
            </div>
          )}
        </div>
      )}

      {s.services && s.services.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {s.services.map((sv) => (
            <span
              key={sv}
              className="inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border border-zinc-300 bg-zinc-50 text-zinc-700"
            >
              {sv}
            </span>
          ))}
        </div>
      )}

      {s.notes && (
        <p className="mt-3 text-[12px] text-zinc-500 leading-relaxed italic">{s.notes}</p>
      )}

      <div className="mt-5 flex flex-wrap gap-2 pt-4 border-t border-zinc-200">
        {s.website && (
          <a
            href={s.website}
            target="_blank"
            rel="noreferrer"
            className="btn-outline text-xs"
            data-testid={`shelter-website-${s.id}`}
          >
            <ExternalLink size={12} /> Website
          </a>
        )}
        <a
          href={dirUrl}
          target="_blank"
          rel="noreferrer"
          className="btn-danger text-xs"
          data-testid={`shelter-directions-${s.id}`}
        >
          <Navigation2 size={12} /> Get Directions
        </a>
      </div>
    </div>
  );
}

export default function LocalShelters() {
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [radiusMi, setRadiusMi] = useState(null);
  const [origin, setOrigin] = useState(null);
  const [q, setQ] = useState("");
  const [service, setService] = useState("");
  const [state, setState] = useState("");
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        const { data } = await api.get("/shelters", {
          params: {
            q: q || undefined,
            service: service || undefined,
            state: state || undefined,
            limit: 200,
          },
        });
        if (!cancelled) {
          setItems(data.items || []);
          if (data.services) setServices(data.services);
          setRadiusMi(typeof data.radius_mi === "number" ? data.radius_mi : null);
          setOrigin(data.origin || null);
        }
      } catch (err) {
        if (!cancelled) {
          setErrorMsg("Couldn't load the shelter directory. Please try again in a moment.");
          toast.error("Directory load failed.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [q, service, state]);

  const uniqueStates = useMemo(() => {
    const s = new Set(items.map((i) => i.state_code).filter(Boolean));
    return Array.from(s).sort();
  }, [items]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10" data-testid="local-shelters-page">
      <SEO
        title="Local Shelters & Animal Resources · Paws on Record"
        description="A public directory of animal shelters, animal-control agencies, and animal-protection nonprofits. Search by city or ZIP. Not affiliated with or endorsed by Paws on Record unless specifically identified as a partner."
        path="/local-shelters"
      />

      <div className="label-eyebrow">Section · Public Resource</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">
        Local Shelters &amp; Animal Resources.
      </h1>
      <p className="text-lg text-zinc-700 mt-4 max-w-3xl leading-relaxed">
        A public directory of animal shelters, animal-control agencies and
        animal-protection nonprofits. Search by city or ZIP to find help
        near you.
      </p>

      {/* Search bar */}
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="flex items-center border border-zinc-900 bg-white max-w-lg flex-1 min-w-[240px]">
          <Search size={16} className="ml-3 text-zinc-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by city, ZIP, or shelter name…"
            className="w-full px-3 py-3 outline-none text-base"
            data-testid="shelter-search"
          />
        </div>
        <select
          value={state}
          onChange={(e) => setState(e.target.value)}
          className="border border-zinc-900 px-3 py-3 bg-white text-sm"
          data-testid="shelter-state-filter"
        >
          <option value="">All states</option>
          {uniqueStates.map((s) => (<option key={s} value={s}>{s}</option>))}
        </select>
        <select
          value={service}
          onChange={(e) => setService(e.target.value)}
          className="border border-zinc-900 px-3 py-3 bg-white text-sm"
          data-testid="shelter-service-filter"
        >
          <option value="">All services</option>
          {services.map((s) => (<option key={s} value={s}>{s}</option>))}
        </select>
      </div>

      {errorMsg && (
        <div className="mt-8 border border-red-600 bg-red-50 text-red-900 p-4 flex items-start gap-3" data-testid="shelter-error">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div className="text-sm leading-relaxed">{errorMsg}</div>
        </div>
      )}

      {/* Results */}
      <div className="mt-8">
        {loading ? (
          <div className="text-sm text-zinc-500 flex items-center gap-2" data-testid="shelter-loading">
            <Loader2 size={14} className="animate-spin" /> Loading directory…
          </div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-zinc-400 p-10 text-center" data-testid="shelter-empty">
            <Building2 size={32} className="mx-auto text-zinc-400" />
            <div className="font-display font-black text-xl mt-3">
              No shelters match those filters.
            </div>
            <p className="text-sm text-zinc-500 mt-2">
              {/^\d{5}$/.test(q.trim())
                ? "We couldn't find any shelters within 250 miles of that ZIP. Try a nearby ZIP or search by city name."
                : "Try a broader search or clear the state / service filter."}
            </p>
          </div>
        ) : (
          <>
            {radiusMi && origin?.zip && (
              <div
                className="mb-5 border border-red-700 bg-red-50 p-4 flex items-start gap-3 text-sm text-red-900"
                data-testid="shelter-radius-banner"
              >
                <Navigation2 size={16} className="mt-0.5 shrink-0" />
                <div className="leading-relaxed">
                  Showing the nearest shelters within{" "}
                  <strong>{radiusMi} miles of {origin.zip}</strong>
                  {origin.display_name && (
                    <span className="text-red-800/80"> · {origin.display_name.split(",").slice(0, 2).join(", ").trim()}</span>
                  )}
                  . Sorted by distance.
                </div>
              </div>
            )}
            <div className="mb-4 label-eyebrow" data-testid="shelter-count">
              <span className="font-display font-black text-xl mr-2 align-middle">{items.length}</span>
              result{items.length === 1 ? "" : "s"}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="shelter-grid">
              {items.map((s) => (<ShelterCard key={s.id} s={s} />))}
            </div>
          </>
        )}
      </div>

      {/* Non-endorsement disclaimer (verbatim from user spec) */}
      <div
        className="mt-14 border-t-2 border-zinc-900 pt-6 max-w-3xl text-[13px] text-zinc-600 leading-relaxed"
        data-testid="shelter-disclaimer"
      >
        <span className="inline-block label-eyebrow mb-2">Public resource · not an endorsement</span>
        <br />
        Shelter information is provided as a public resource. A listing does
        not indicate affiliation with or endorsement of Paws on Record unless
        specifically identified as a Paws on Record partner. If you spot
        incorrect information, please <Link to="/report" className="underline">contact us</Link> and we'll update the record.
      </div>
    </div>
  );
}
